/**
 * MfaEnrollment.js
 *
 * TOTP MFA enrollment for Raider Depot admins.
 * Handles: re-authentication → secret generation → QR display → verification → finalize.
 *
 * CR-007 / E-2: Admin MFA/TOTP
 */
import React, { useState } from "react";
import {
  multiFactor,
  TotpMultiFactorGenerator,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import S from "./adminStyles";

export default function MfaEnrollment({ user, onClose }) {
  // Steps: "reauth" → "qr" → "verify" → "done"
  const [step, setStep] = useState("reauth");
  const [password, setPassword] = useState("");
  const [totpSecret, setTotpSecret] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState("");
  const [enrolledFactors, setEnrolledFactors] = useState(
    multiFactor(user).enrolledFactors || [],
  );

  const isAlreadyEnrolled = enrolledFactors.some((f) => f.factorId === "totp");

  // Step 1: Re-authenticate with current password
  const handleReauth = async () => {
    setError("");
    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      // Step 2: Generate TOTP secret
      const session = await multiFactor(user).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      setTotpSecret(secret);
      setStep("qr");
    } catch (err) {
      if (
        err.code === "auth/wrong-password" ||
        err.code === "auth/invalid-credential"
      ) {
        setError("Incorrect password. Please try again.");
      } else {
        setError("Authentication error: " + err.message);
      }
    }
  };

  // Step 3: Verify code and finalize enrollment
  const handleVerify = async () => {
    setError("");
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        verificationCode,
      );
      await multiFactor(user).enroll(assertion, "Authenticator App");
      setStep("done");
      setEnrolledFactors(multiFactor(user).enrolledFactors);
    } catch (err) {
      if (err.code === "auth/invalid-verification-code") {
        setError(
          "Invalid code. Please check your authenticator app and try again.",
        );
      } else {
        setError("Enrollment error: " + err.message);
      }
    }
  };

  // Generate the otpauth:// URI for QR code display
  const getQrUri = () => {
    if (!totpSecret) return "";
    return totpSecret.generateQrCodeUrl(user.email, "Raider Depot");
  };

  // ── Already enrolled ──
  if (isAlreadyEnrolled && step !== "done") {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <h2 style={styles.title}>MFA Already Enabled</h2>
          <p style={styles.text}>
            Your account is protected with an authenticator app.
          </p>
          <button
            style={{ ...S.btnPrimary, marginTop: "16px" }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* ── Step: Re-authenticate ── */}
        {step === "reauth" && (
          <>
            <h2 style={styles.title}>Enable Two-Factor Authentication</h2>
            <p style={styles.text}>
              Enter your current password to begin MFA setup.
            </p>
            <input
              style={{ ...S.input, marginBottom: "12px" }}
              type="password"
              placeholder="Current password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleReauth()}
            />
            {error && <p style={styles.error}>{error}</p>}
            <div style={styles.btnRow}>
              <button
                style={S.btnSecondary || styles.btnCancel}
                onClick={onClose}
              >
                Cancel
              </button>
              <button style={S.btnPrimary} onClick={handleReauth}>
                Continue
              </button>
            </div>
          </>
        )}

        {/* ── Step: QR Code ── */}
        {step === "qr" && (
          <>
            <h2 style={styles.title}>Scan QR Code</h2>
            <p style={styles.text}>
              Open your authenticator app (Google Authenticator, Authy, or
              similar) and scan this QR code:
            </p>
            <div style={styles.qrContainer}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(getQrUri())}`}
                alt="TOTP QR Code"
                style={{ width: 200, height: 200 }}
              />
            </div>
            <p style={{ ...styles.text, fontSize: "11px", color: "#94a3b8" }}>
              Can't scan? Manual key:{" "}
              <code style={styles.code}>{totpSecret?.secretKey}</code>
            </p>
            <input
              style={{ ...S.input, marginBottom: "12px", marginTop: "12px" }}
              type="text"
              inputMode="numeric"
              placeholder="6-digit code from app"
              value={verificationCode}
              onChange={(e) =>
                setVerificationCode(
                  e.target.value.replace(/\D/g, "").slice(0, 6),
                )
              }
              onKeyDown={(e) =>
                e.key === "Enter" &&
                verificationCode.length === 6 &&
                handleVerify()
              }
            />
            {error && <p style={styles.error}>{error}</p>}
            <div style={styles.btnRow}>
              <button
                style={S.btnSecondary || styles.btnCancel}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                style={{
                  ...S.btnPrimary,
                  opacity: verificationCode.length === 6 ? 1 : 0.5,
                }}
                onClick={handleVerify}
                disabled={verificationCode.length !== 6}
              >
                Verify & Enable
              </button>
            </div>
          </>
        )}

        {/* ── Step: Done ── */}
        {step === "done" && (
          <>
            <h2 style={styles.title}>MFA Enabled ✓</h2>
            <p style={styles.text}>
              Two-factor authentication is now active on your account. You'll
              need your authenticator app each time you sign in.
            </p>
            <button
              style={{ ...S.btnPrimary, marginTop: "16px" }}
              onClick={onClose}
            >
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#fff",
    borderRadius: "12px",
    padding: "32px",
    maxWidth: "420px",
    width: "90%",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#1e293b",
    marginBottom: "8px",
  },
  text: {
    fontSize: "14px",
    color: "#64748b",
    lineHeight: 1.5,
    marginBottom: "16px",
  },
  error: {
    color: "#dc2626",
    fontSize: "13px",
    marginBottom: "8px",
  },
  btnRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "16px",
  },
  btnCancel: {
    background: "none",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "14px",
    cursor: "pointer",
    color: "#64748b",
  },
  qrContainer: {
    display: "flex",
    justifyContent: "center",
    margin: "16px 0",
    padding: "16px",
    background: "#f8fafc",
    borderRadius: "8px",
  },
  code: {
    background: "#f1f5f9",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "12px",
    fontFamily: "monospace",
    wordBreak: "break-all",
  },
};
